import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { decryptData } from "../../utils/dataEncoder";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  fetchLocationIncidentRecordsSuccess,
  fetchLocationIncidentRecordsFailure,
  fetchResidentIncidentRecordsSuccess,
  fetchResidentIncidentRecordsFailure,
  fetchIncidentRecordSuccess,
  fetchIncidentRecordFailure,
  createIncidentRecordSuccess,
  createIncidentRecordFailure,
  updateIncidentRecordSuccess,
  updateIncidentRecordFailure,
  addIncidentNotificationSuccess,
  addIncidentNotificationFailure,
  escalateIncidentSafeguardingSuccess,
  escalateIncidentSafeguardingFailure,
  closeIncidentRecordSuccess,
  closeIncidentRecordFailure,
  reopenIncidentRecordSuccess,
  reopenIncidentRecordFailure,
} from "./actions";
import {
  FETCH_LOCATION_INCIDENT_RECORDS,
  FETCH_RESIDENT_INCIDENT_RECORDS,
  FETCH_INCIDENT_RECORD,
  CREATE_INCIDENT_RECORD,
  UPDATE_INCIDENT_RECORD,
  ADD_INCIDENT_NOTIFICATION,
  ESCALATE_INCIDENT_SAFEGUARDING,
  CLOSE_INCIDENT_RECORD,
  REOPEN_INCIDENT_RECORD,
} from "./constants";

// Build the register query string. Array filters go out as `key[]=value`
// (whereIn on the API); scalars are coerced to a single-element array.
const buildRegisterQuery = (filters = {}, page, perPage) => {
  const params = new URLSearchParams();
  const appendArray = (key, values) => {
    const list = Array.isArray(values) ? values : values ? [values] : [];
    list
      .filter((v) => v !== null && v !== undefined && v !== "")
      .forEach((v) => params.append(`${key}[]`, v));
  };
  appendArray("status", filters.status);
  appendArray("classification", filters.classification);
  appendArray("incident_type", filters.incident_type);
  appendArray("harm_level", filters.harm_level);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.resident_id) params.set("resident_id", filters.resident_id);
  if (filters.search) params.set("search", filters.search);
  if (page) params.set("page", page);
  if (perPage) params.set("per_page", perPage);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

// The api util decrypts error bodies locally to build the message string but
// discards the payload; re-derive the envelope so the close gate can read
// { requires_override, missing_bodies } off `.data`.
const responseBody = (err) => {
  const raw = err?.response?.data;
  if (!raw) return null;
  if (raw.encrypted === true && typeof raw.data === "string") {
    try {
      return decryptData(raw.data);
    } catch (_) {
      return null;
    }
  }
  return raw;
};

// Fetch the location register — payload: { locationId, filters, page, perPage }.
export function* fetchLocationIncidentRecordsSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId, filters, page, perPage } = payload || {};
  try {
    const query = buildRegisterQuery(filters, page, perPage);
    const { data } = yield call(
      api(token).get,
      `/api/location/${locationId}/incident-records${query}`,
    );
    yield put(fetchLocationIncidentRecordsSuccess(data || {}));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(fetchLocationIncidentRecordsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching the incident register!"));
  }
}

// Fetch a resident's records — payload: { residentId }.
export function* fetchResidentIncidentRecordsSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId } = payload || {};
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident/${residentId}/incident-record`,
    );
    const records = data?.records || [];
    yield put(fetchResidentIncidentRecordsSuccess(records));
    typeof onSuccess === "function" && onSuccess(records);
  } catch (e) {
    yield put(fetchResidentIncidentRecordsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching incident records!"));
  }
}

// Hydrate the drawer — payload: { residentId, uuid }.
export function* fetchIncidentRecordSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, uuid } = payload || {};
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident/${residentId}/incident-record/${uuid}`,
    );
    yield put(fetchIncidentRecordSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(fetchIncidentRecordFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching incident record!"));
  }
}

// Create a manual record — payload: { residentId, data }.
export function* createIncidentRecordSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, data: body } = payload || {};
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/incident-record`,
      body,
    );
    yield put(createIncidentRecordSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
    yield put(successAlert("Incident record created."));
  } catch (e) {
    yield put(createIncidentRecordFailure());
    typeof onError === "function" && onError(e);
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while creating incident record!"));
  }
}

// Idempotent per-step save — payload: { residentId, uuid, data }.
export function* updateIncidentRecordSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, uuid, data: body } = payload || {};
  try {
    const { data } = yield call(
      api(token).patch,
      `/api/resident/${residentId}/incident-record/${uuid}`,
      body,
    );
    yield put(updateIncidentRecordSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
    yield put(successAlert("Saved."));
  } catch (e) {
    yield put(updateIncidentRecordFailure());
    typeof onError === "function" && onError(e);
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while saving incident record!"));
  }
}

// Add a statutory notification — payload: { residentId, uuid, data }.
export function* addIncidentNotificationSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, uuid, data: body } = payload || {};
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/incident-record/${uuid}/notification`,
      body,
    );
    yield put(addIncidentNotificationSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
    yield put(successAlert("Notification recorded."));
  } catch (e) {
    yield put(addIncidentNotificationFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while recording notification!"));
  }
}

// Escalate to a safeguarding review — payload:
// { residentId, uuid, safeguardingReviewId? }.
export function* escalateIncidentSafeguardingSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, uuid, safeguardingReviewId } = payload || {};
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/incident-record/${uuid}/escalate-safeguarding`,
      safeguardingReviewId
        ? { safeguarding_review_id: safeguardingReviewId }
        : {},
    );
    yield put(escalateIncidentSafeguardingSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
    yield put(successAlert("Safeguarding review linked."));
  } catch (e) {
    yield put(escalateIncidentSafeguardingFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while escalating to safeguarding!"));
  }
}

// Close — payload: { residentId, uuid, data }. HTTP 422 is the notification /
// duty-of-candour gate: hand the decrypted body to onGateBlocked so the drawer
// can prompt for override / the missing bodies, instead of a generic toast.
export function* closeIncidentRecordSaga({
  payload,
  onSuccess,
  onGateBlocked,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, uuid, data: body } = payload || {};
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/incident-record/${uuid}/close`,
      body,
    );
    yield put(closeIncidentRecordSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
    yield put(successAlert("Incident record closed."));
  } catch (e) {
    yield put(closeIncidentRecordFailure());
    if (e?.response?.status === 422 && typeof onGateBlocked === "function") {
      onGateBlocked(responseBody(e) || {});
      return;
    }
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while closing incident record!"));
  }
}

// Reopen a closed record — payload: { residentId, uuid }.
export function* reopenIncidentRecordSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, uuid } = payload || {};
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/incident-record/${uuid}/reopen`,
    );
    yield put(reopenIncidentRecordSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
    yield put(successAlert("Incident record reopened."));
  } catch (e) {
    yield put(reopenIncidentRecordFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while reopening incident record!"));
  }
}

export function* fetchLocationIncidentRecordsFlow() {
  yield takeEvery(
    FETCH_LOCATION_INCIDENT_RECORDS,
    fetchLocationIncidentRecordsSaga,
  );
}
export function* fetchResidentIncidentRecordsFlow() {
  yield takeEvery(
    FETCH_RESIDENT_INCIDENT_RECORDS,
    fetchResidentIncidentRecordsSaga,
  );
}
export function* fetchIncidentRecordFlow() {
  yield takeEvery(FETCH_INCIDENT_RECORD, fetchIncidentRecordSaga);
}
export function* createIncidentRecordFlow() {
  yield takeEvery(CREATE_INCIDENT_RECORD, createIncidentRecordSaga);
}
export function* updateIncidentRecordFlow() {
  yield takeEvery(UPDATE_INCIDENT_RECORD, updateIncidentRecordSaga);
}
export function* addIncidentNotificationFlow() {
  yield takeEvery(ADD_INCIDENT_NOTIFICATION, addIncidentNotificationSaga);
}
export function* escalateIncidentSafeguardingFlow() {
  yield takeEvery(
    ESCALATE_INCIDENT_SAFEGUARDING,
    escalateIncidentSafeguardingSaga,
  );
}
export function* closeIncidentRecordFlow() {
  yield takeEvery(CLOSE_INCIDENT_RECORD, closeIncidentRecordSaga);
}
export function* reopenIncidentRecordFlow() {
  yield takeEvery(REOPEN_INCIDENT_RECORD, reopenIncidentRecordSaga);
}

export default function* IncidentRecordSaga() {
  yield all([
    fetchLocationIncidentRecordsFlow(),
    fetchResidentIncidentRecordsFlow(),
    fetchIncidentRecordFlow(),
    createIncidentRecordFlow(),
    updateIncidentRecordFlow(),
    addIncidentNotificationFlow(),
    escalateIncidentSafeguardingFlow(),
    closeIncidentRecordFlow(),
    reopenIncidentRecordFlow(),
  ]);
}
